import React, { useEffect, useRef } from "react";

// Declare bootstrap property on window object
declare global {
    interface Window {
        bootstrap: any;
    }
}
import { toFixedThree } from "../../../utility/helper";
const getValueByPath = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

interface Column<T> {
    header: string; // Column header
    accessor: keyof T | string; // Key to access data or custom property
    render?: (value: any, row: T) => React.ReactNode; // Optional custom rendering function
}

interface Action<T> {
    icon: string; // Icon class for the action
    color: string; // Button color (e.g., "success", "danger")
    tooltip?: string; // 
    onClick: (row: T) => void; // Click handler for the action
    show?: (row: T) => boolean; // Function to determine if the action should be shown
}

interface TableProps<T> {
    columns: Column<T>[]; // Array of columns
    data: T[]; // Array of data
    loading: boolean; // Loading state
    emptyMessage?: string; // Message to display when no data is available
    actions?: Action<T>[]; // Optional row-level actions
}

const Table = <T,>({
    columns,
    data,
    loading,
    emptyMessage = "No data available.",
    actions,
}: TableProps<T>): JSX.Element => {
    const tableRef = useRef<HTMLTableElement>(null);
    useEffect(() => {
        if (!tableRef.current) return;

        // Initialize tooltips
        const initTooltips = () => {
            const tooltipTriggerList = tableRef.current!.querySelectorAll('[data-bs-toggle="tooltip"]');
            Array.from(tooltipTriggerList).forEach(tooltipTriggerEl => {
                new window.bootstrap.Tooltip(tooltipTriggerEl, {
                    boundary: tableRef.current!,
                    trigger: 'hover'
                });
            });
        };

        // Add slight delay to ensure DOM is ready
        const timeoutId = setTimeout(initTooltips, 50);

        // Cleanup
        return () => {
            clearTimeout(timeoutId);
            const tooltipList = tableRef?.current?.querySelectorAll('.tooltip');
            tooltipList?.forEach(tooltip => {
                const instance = window.bootstrap.Tooltip.getInstance(tooltip);
                instance?.dispose();
            });
        };
    }, [data]);


    const clearToolTip = (e: any) => {
        const tooltipInstance = window.bootstrap.Tooltip.getInstance(e.currentTarget);
        tooltipInstance?.dispose();
    }
    return (
        <div className=" table-responsive">
            <table className="table app-table-hover mb-0 text-left" ref={tableRef}>
                <thead>
                    <tr>
                        {columns.map((column, index) => (
                            <th key={index} className="cell text-nowrap">
                                {column.header}
                            </th>
                        ))}
                        {actions && <th className="cell">Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td className="text-center" colSpan={columns.length + (actions ? 1 : 0)}>
                                Loading...
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr>
                            <td className="text-center" colSpan={columns.length + (actions ? 1 : 0)}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        data.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {columns.map((column, colIndex) => {
                                    const value = typeof column.accessor === "string"
                                        ? getValueByPath(row, column.accessor)
                                        : row[column.accessor as keyof T];

                                    return (
                                        <td key={colIndex} className="cell">
                                            {column.render ? column.render(value, row) : typeof value === 'number' ? toFixedThree(value) : value}
                                        </td>
                                    );
                                })}
                                {actions && (
                                    <td className="cell">
                                        <div className="d-flex align-items-center gap-1">
                                            {actions.map((action, actionIndex) => (
                                                <>
                                                    {
                                                        action.show ? action.show(row) &&
                                                            <button
                                                                key={actionIndex}
                                                                className={`btn btn-${action.color} px-2 py-1 text-white`}
                                                                onClick={(e) => {
                                                                    action.onClick(row);
                                                                    clearToolTip(e)
                                                                }}
                                                                data-bs-toggle='tooltip'
                                                                data-bs-title={action?.tooltip}
                                                            >
                                                                <i className={action.icon}></i>
                                                            </button> : <button
                                                                key={actionIndex}
                                                                className={`btn btn-${action.color} px-2 py-1 text-white`}
                                                                onClick={(e) => {
                                                                    action.onClick(row);
                                                                    clearToolTip(e)
                                                                }}

                                                                data-bs-toggle='tooltip'
                                                                data-bs-title={action?.tooltip}

                                                            >
                                                            <i className={action.icon}></i>
                                                        </button>

                                                    }

                                                </>
                                            ))}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default Table;
